import React from 'react';
import { Link } from 'react-router-dom';
import { format as formatDate, eachDayOfInterval, startOfDay, endOfDay, addMonths, addDays, } from 'date-fns';
import { keyBy } from 'lodash';
import { Badge } from 'reactstrap';
import { useAsync, } from 'react-use';

import firebase, { getFirestore, collection, where, query, getCountFromServer } from '../../firebase';
import AdminPage from '../hocs/AdminPage';
import UserListSection from '../UserListSection';
import useQueryParams from '../hooks/useQueryParams';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import { fields, scheduleTypes } from '../../shared/models/customerJourney';
import { deliveryMethods } from '../../shared/config';
import { emailDeliveryStatuses, isFailedDeliveryStatus } from '../../shared/models/emailDeliveryStatus';
import useUserTags from '../hooks/useUserTags';

const db = firebase.firestore();
const customerJourneysRef = db.collection('customerJourneys');
const failedDeliveryStatuses = Object.keys(emailDeliveryStatuses).filter(isFailedDeliveryStatus);

export default AdminPage(function AdminCustomerJourneyHistories(props) {
  const {
    user,
    match: {
      params: { customerJourneyId },
    },
  } = props;
  const params = useQueryParams();
  const collectionPath = `customerJourneys/${customerJourneyId}/deliveryHistories`;
  const usersRef = db.collection(collectionPath).where('customerJourneyRowKey', '==', params.key);
  const prefixColumns = [
    {
      name: 'createdAt',
      label: (_) => '配信日時',
      body: ({ orderId, createdAt }) => {
        const Component = orderId != null ? Link : 'div';
        return (
          <Component to={`/admin/orders/${orderId}`}>{formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}</Component>
        );
      },
    },
    {
      name: 'deliveryStatus',
      label: (_) => '配信結果',
      body: ({ deliveryStatus, sendgrid }) => {
        const status = emailDeliveryStatuses[deliveryStatus];
        if (status == null) return <span className="text-muted">-</span>;
        return (
          <div className="text-nowrap">
            <Badge color={status.color}>{status.label}</Badge>
            {sendgrid?.reason && <div className="small text-muted">{sendgrid.reason}</div>}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <CustomerJourneryRowTable customerJourneyId={customerJourneyId} customerJourneyRowKey={params.key} />
      <StatTable customerJourneyId={customerJourneyId} customerJourneyRowKey={params.key} />
      <UserListSection
        user={user}
        usersRef={usersRef}
        title="配信履歴"
        prefixColumns={prefixColumns}
        renderCountSuffix={(histories) => {
          const failedCount = histories.filter((_) => isFailedDeliveryStatus(_.deliveryStatus)).length;
          if (failedCount === 0) return null;
          return <span className="ml-2 text-danger font-weight-bold">うち未配信 {failedCount} 件</span>;
        }}
        usersRefV9={query(collection(getFirestore(), collectionPath), where('customerJourneyRowKey', '==', params.key))}
      />
    </>
  );
});

const CustomerJourneryRowTable = (props) => {
  const { customerJourneyId, customerJourneyRowKey } = props;
  const customerJourney = useDocumentSubscription(customerJourneysRef.doc(customerJourneyId), [customerJourneyId]);
  const row = customerJourney?.customerJourneyRows.find((_) => _.key === customerJourneyRowKey);
  const userTags = useUserTags();
  const userTagsById = keyBy(userTags, 'id');
  if (!customerJourney || !row) return <></>;
  const { key, name, deliveryMethod, scheduleType, daysAfter, subject, body, isSuspended, userTagId } = row;
  return (
    <div className="container-fluid py-5 position-relative d-flex flex-column gap-3">
      <table className="table">
        <thead className="thead-light text-center text-nowrap">
          <tr>
            <th>名称</th>
            <th>配信方式</th>
            <th>スケジュール種別</th>
            <th>Eメール件名</th>
            <th>本文</th>
            <th>配信停止</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ whiteSpace: 'pre-line' }}> {name}</td>
            <td style={{ whiteSpace: 'pre-line' }}> {deliveryMethods[deliveryMethod].label}</td>
            <td style={{ whiteSpace: 'pre-line' }}>
              {scheduleType === 'tag' ? (
                <>
                  <Badge color="secondary" className="mx-1">
                    {userTagsById[userTagId]?.name}
                  </Badge>
                  {scheduleTypes[scheduleType].label}
                </>
              ) : (
                <>
                  {scheduleTypes[scheduleType].label}
                  {daysAfter}日後
                </>
              )}
            </td>
            <td style={{ whiteSpace: 'pre-line' }}>{subject}</td>
            <td style={{ whiteSpace: 'pre-line' }}>{body}</td>
            <td style={{ whiteSpace: 'pre-line' }}>{isSuspended}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const StatTable = (props) => {
  const { customerJourneyId, customerJourneyRowKey } = props;
  const params = useQueryParams();
  const startOn = params.dateRange?.[0] ? new Date(params.dateRange[0]) : addDays(addMonths(new Date(), -6), 1);
  const endOn = params.dateRange?.[1] ? new Date(params.dateRange[1]) : new Date();
  const dates = eachDayOfInterval({ start: startOn, end: endOn }).reverse();
  const customerJourney = useDocumentSubscription(customerJourneysRef.doc(customerJourneyId), [customerJourneyId]);
  // const row = customerJourney?.customerJourneyRows.find((_) => _.key === customerJourneyRowKey);
  const { value: rows } = useAsync(async () => {
    return await Promise.all(dates.map(async (date) => {
      const baseQuery = [
        collection(getFirestore(), `customerJourneys/${customerJourneyId}/deliveryHistories`),
        where('customerJourneyRowKey', '==', customerJourneyRowKey),
        where('createdAt', '>=', startOfDay(date)),
        where('createdAt', '<=', endOfDay(date)),
      ];
      const [count, failedCount] = await Promise.all([
        getCountFromServer(query(...baseQuery)).then((_) => _.data().count),
        // NOTE: 複合インデックス作成中は失敗するので、配信数の表示までは巻き込まない
        getCountFromServer(query(...baseQuery, where('deliveryStatus', 'in', failedDeliveryStatuses)))
          .then((_) => _.data().count)
          .catch(() => null),
      ]);
      return { date, count, failedCount };
    }));
  }, [customerJourneyId, customerJourneyRowKey]);
  return (
    <div className="container py-5 position-relative d-flex flex-column gap-3">
      <table className="table bg-white table-bordered table-sm table-condense">
        <thead className="thead-light text-center text-nowrap">
          <tr>
            <th>日付</th>
            <th>配信数</th>
            <th>未配信数</th>
          </tr>
        </thead>
        <tbody>
          {
            rows?.map((row) => {
              const { date, count, failedCount } = row;
              return (
                <tr key={date.toString()}>
                  <td>
                    {formatDate(date, 'yyyy/MM/dd')}
                  </td>
                  <td>
                    {count.toLocaleString()}
                  </td>
                  <td className={failedCount > 0 ? 'text-danger font-weight-bold' : ''}>
                    {failedCount != null ? failedCount.toLocaleString() : '-'}
                  </td>
                </tr>
              );
            })
          }
        </tbody>
      </table>
    </div>
  );
};
